import type { ReactElement } from "react";
import {
    DATA_GRID_SKELETON_PROPS,
    type DataGridSkeleton,
    type DataGridSkeletonPreset,
} from "./skeletonTypes";
import styles from "./skeletonShapes.module.scss";

interface SkeletonPosition {
    rowIndex: number;
    columnIndex: number;
}

type FixedWidthPreset = "date" | "dateWithYear" | "number" | "money" | "tag";

/**
 * Typical rendered widths in px at the cell font (14px Inter): "Aug 5" to
 * "Jan 31", "Jan 31, 2026", short counts, "$42.00" to "- $7,000.00", and
 * one-word Tags ("Draft" is 47px).
 */
const FIXED_WIDTHS: Record<FixedWidthPreset, readonly number[]> = {
    date: [39, 45, 41, 46],
    dateWithYear: [85, 89, 86, 90],
    number: [18, 9, 27, 36],
    money: [64, 49, 72, 56, 78],
    tag: [47, 64, 56, 72],
};

/** Deterministic per cell, so skeletons never flicker between renders. */
const seedOf = ({ rowIndex, columnIndex }: SkeletonPosition) =>
    rowIndex * 17 + columnIndex * 29;

const textWidth = (position: SkeletonPosition) =>
    `${45 + (seedOf(position) % 40)}%`;

const fixedWidth = (preset: FixedWidthPreset, position: SkeletonPosition) => {
    const widths = FIXED_WIDTHS[preset];
    return `${widths[seedOf(position) % widths.length]}px`;
};

/** Row numbers are tabular digits, one `ch` each. */
const rowNumberWidth = ({ rowIndex }: SkeletonPosition) =>
    `${String(rowIndex + 1).length}ch`;

const Shape = ({ className, width }: { className: string; width?: string }) => (
    <span
        {...DATA_GRID_SKELETON_PROPS}
        className={className}
        style={width === undefined ? undefined : { width }}
    />
);

/** A leading visual (avatar, logo, icon, dot) and a text bar, at the cell's gap. */
const WithBar = ({
    className,
    leadingClassName,
    position,
}: {
    className: string;
    leadingClassName: string;
    position: SkeletonPosition;
}) => (
    <span className={className}>
        <Shape className={leadingClassName} />
        <Shape className={styles.bar} width={textWidth(position)} />
    </span>
);

const renderPreset = (
    preset: DataGridSkeletonPreset,
    position: SkeletonPosition,
): ReactElement | null => {
    switch (preset) {
        case "none":
            return null;
        case "text":
            return <Shape className={styles.bar} width={textWidth(position)} />;
        case "rowNumber":
            return (
                <Shape
                    className={styles.bar}
                    width={rowNumberWidth(position)}
                />
            );
        case "date":
        case "dateWithYear":
        case "number":
        case "money":
            return (
                <Shape
                    className={styles.bar}
                    width={fixedWidth(preset, position)}
                />
            );
        case "tag":
            return (
                <Shape
                    className={styles.tag}
                    width={fixedWidth(preset, position)}
                />
            );
        case "indicator":
            return (
                <WithBar
                    className={styles.status}
                    leadingClassName={styles.dot}
                    position={position}
                />
            );
        case "statusIcon":
            return (
                <WithBar
                    className={styles.status}
                    leadingClassName={styles.icon}
                    position={position}
                />
            );
        case "avatar":
            return <Shape className={styles.avatar} />;
        case "avatarText":
            return (
                <WithBar
                    className={styles.leading}
                    leadingClassName={styles.avatar}
                    position={position}
                />
            );
        case "avatarGroup":
            return <Shape className={styles.avatarGroup} />;
        case "institutionText":
            return (
                <WithBar
                    className={styles.leading}
                    leadingClassName={styles.institution}
                    position={position}
                />
            );
        case "fileText":
            return (
                <WithBar
                    className={styles.leading}
                    leadingClassName={styles.icon}
                    position={position}
                />
            );
        case "checkbox":
            return <span className={styles.checkbox} />;
        case "control":
            return <Shape className={styles.control} />;
    }
};

interface SkeletonShapeProps extends SkeletonPosition {
    skeleton: DataGridSkeleton;
    /** A `tree` column: the placeholder starts after the chevron slot. */
    isTree: boolean;
}

/** One skeleton cell's placeholder: a preset shape or the column's own. */
export const SkeletonShape = ({
    skeleton: Skeleton,
    isTree,
    ...position
}: Readonly<SkeletonShapeProps>) => {
    const shape =
        typeof Skeleton === "string" ? (
            renderPreset(Skeleton, position)
        ) : (
            <Skeleton rowIndex={position.rowIndex} />
        );
    return isTree ? <span className={styles.tree}>{shape}</span> : shape;
};
